<template>
  <div id="autocomplete-component">
    <div
      class="flex rounded-16 items-center bg-white shadow-normal pl-4 pr-3"
      :class="{
        'z-10 fixed lg:!relative left-0 right-0 top-0 w-full transition ease-in-out duration-500':
         params['is_sm_scroll'] === 'yes' && isScrolled && (!isHomepage || !isCompanyPage),
        'py-3.5 md:py-0': isHomepage,
        'py-3.5 md:py-0 pl-6 rounded-16 box-shadow-custom-shadow h-16':
          isCompanyPage,
      }"
    >
      <span
        :class="{
          'hidden lg:block': !isHomepage && !isCompanyPage,
          'ml-12px': isCompanyPage,
        }"
      >
        <i
          class="feather icon-map-pin icon text-lg text-primary-darker"
          :class="{
            'mr-3': isHomepage || isCompanyPage,
          }"
        />
      </span>
      <div class="w-full flex items-center justify-between py-3">
        <div class="w-full" ref="autocompleteContainer"></div>
        <div class="relative" v-show="!isHomepage && !isCompanyPage">
          <ais-current-refinements>
            <template #default="{ items }">
              <span
                class="badge absolute w-5 h-5 text-white bg-primary-darker text-12 inline-flex items-center justify-center z-50 lg:hidden"
                v-if="getSelectedFiltersCount(items) !== 0"
              >
                {{ selectedFiltersCount }}
              </span>
              <vs-button
                color="#ffffff"
                type="flat"
                class="mobile_filter cursor-pointer lg:hidden px-2 py-2 mr-2"
                @click="$root.$emit('openMobileFilters')"
                :class="{ filterIsActive: selectedFiltersCount !== 0 }"
              >
                <svg
                  class="mobileFilterIcon"
                  xmlns="http://www.w3.org/2000/svg"
                  width="24"
                  height="24"
                  viewBox="0 0 24 24"
                >
                  <path
                    :fill="params['font-color'] ?? '#d54429'"
                    fill-rule="evenodd"
                    d="M9.01 15.351c1.237 0 2.289.864 2.619 2.045h7.608c.433 0 .763.337.763.78 0 .442-.35.78-.763.78H11.63C11.299 20.136 10.247 21 9.01 21c-1.237 0-2.288-.864-2.618-2.044h-1.63c-.432 0-.762-.338-.762-.78 0-.443.35-.78.763-.78h1.629c.33-1.18 1.381-2.045 2.618-2.045zm0 1.56c-.7 0-1.237.548-1.237 1.265 0 .716.578 1.264 1.237 1.264.66 0 1.237-.548 1.237-1.264 0-.717-.536-1.265-1.237-1.265zm6.042-7.735c1.178 0 2.188.783 2.566 1.878l.052.166h1.567c.433 0 .763.358.742.78 0 .406-.294.723-.66.773l-.103.007H17.65c-.33 1.18-1.381 2.044-2.618 2.044-1.178 0-2.188-.783-2.567-1.878l-.052-.166h-7.65C4.33 12.78 4 12.422 4 12c0-.406.295-.723.661-.773l.102-.007h7.65c.35-1.16 1.401-2.044 2.639-2.044zm0 1.56c-.701 0-1.238.547-1.238 1.264s.578 1.265 1.238 1.265 1.237-.548 1.237-1.265c0-.717-.536-1.265-1.237-1.265zM9.01 3c1.179 0 2.188.784 2.567 1.878l.052.166h7.608c.433 0 .763.338.763.78 0 .406-.295.723-.661.773l-.102.007H11.63c-.33 1.18-1.382 2.045-2.619 2.045-1.178 0-2.188-.784-2.566-1.878l-.052-.167h-1.63c-.432 0-.762-.337-.762-.78 0-.405.295-.723.661-.773l.102-.007h1.629C6.722 3.864 7.773 3 9.01 3zm0 1.56c-.66 0-1.237.59-1.237 1.264 0 .675.536 1.265 1.237 1.265.701 0 1.237-.548 1.237-1.265 0-.716-.577-1.264-1.237-1.264z"
                  />
                </svg>
              </vs-button>
            </template>
          </ais-current-refinements>
        </div>
        <primary-button
          v-if="isHomepage || isCompanyPage"
          @click="performPageSpecificAction(autocompleteQuery)"
          :disabled="isLoading"
          :class="
            isCompanyPage ? 'hidden md:block mr-3 w-111px' : 'hidden md:block'
          "
          :size="isLocationsPage || isCompanyPage ? 'default' : 'large'"
          :bgColor="
            isLocationsPage
              ? params['search-btn-bg-color'] ?? 'var(--primary)'
              : 'var(--primary)'
          "
        >
          Search
          <span v-if="isLoading" class="inline-flex ml-1">
            <i class="feather icon-loader animate-spin"></i>
          </span>
        </primary-button>
        <primary-button
          v-else
          @click="updateAutocompleteInstanceState(autocompleteQuery)"
          :disabled="isLoading"
          :radius="isLoading"
          :icon="isLoading ? 'loader' : 'search'"
          class="alt-btn text-24 inline-flex"
          :class="{
            'animate-spin': isLoading,
          }"
          size="default"
        />
      </div>
    </div>
    <primary-button
      v-show="isHomepage || isCompanyPage"
      @click="performPageSpecificAction(autocompleteQuery)"
      :disabled="isLoading"
      class="md:hidden mt-4"
      size="large"
      :bgColor="
        isLocationsPage
          ? params['search-btn-bg-color'] ?? 'var(--primary)'
          : 'var(--primary)'
      "
    >
      Search
      <span v-if="isLoading" class="inline-flex ml-1">
        <i class="feather icon-loader animate-spin"></i>
      </span>
    </primary-button>
  </div>
</template>

<script>
import { autocomplete } from '@algolia/autocomplete-js';
import { connectSearchBox } from 'instantsearch.js/es/connectors';
import resolveConfig from 'tailwindcss/resolveConfig';
import { createWidgetMixin } from 'vue-instantsearch/vue2/es';

import tailwindConfig from '../../../tailwind.config.js';
import HandleSearchScrollMixin from '../mixins/HandleSearchScrollMixin';
import {
  buildUiState,
  debounced,
  getCityAndStateFromUrl,
  getCityAndStateTitle,
  getCityQueryAndState,
  getUrlParameters,
  isStateAbbrSearch,
  isStateSearch,
  isZipCodeSearch,
  parseURLToState,
  routeToState,
  slugify,
  softDeletedFilter,
  statesWithAbbr,
  storeZipSearchResult,
  addOrRemoveHiddenClassToAutoCompletePanel,
} from '../utils';

const { theme } = resolveConfig(tailwindConfig);
const lgBreakpoint = parseInt(theme.screens.lg.replace('px', ''));
const classesToToggle = ['!top-10', 'lg:!top-12'];
const autocompletePanelTopOffset = 22;
const autocompletePanelYOffset = 16;

async function searchPostalsIndex(vm, query) {
  let filters = `${softDeletedFilter}`;
  const isZipCodeQuery = isZipCodeSearch(query);

  if (isZipCodeQuery) {
    filters += ` AND (code = ${query})`;
  }

  // If we need to limit city searching to the state, uncomment the block below
  /*if (vm.isLocationsPage && vm.locationState) {
    filters += ` AND (state = ${vm.locationState})`;
  }*/

  if (!isZipCodeQuery && !isNaN(parseInt(query))) {
    return [];
  } else {
    const response = await vm.instantSearchInstance.client.search([
      {
        indexName: vm.params['indexes']['postal'],
        params: {
          query: isZipCodeQuery ? '' : query,
          filters,
          attributesToHighlight: ['city_state', 'code', 'state'],
          attributesToRetrieve: ['city_state', 'code', 'state', '_geo'],
          highlightPostTag: '__/aa-highlight__',
          highlightPreTag: '__aa-highlight__',
          hitsPerPage: 10,
        },
      },
    ]);
    return response.results[0].hits;
  }
}

const getPostals = (vm, query) => {
  return debounced([
    {
      sourceId: 'postals',
      async getItems() {
        const hits = await searchPostalsIndex(vm, query);

        if (isZipCodeSearch(query) && hits && hits.length) {
          storeZipSearchResult(query, hits[0]);
        }

        if (query.includes(',')) {
          const { cityQuery: city, state } = getCityQueryAndState(query);
          if (city && state) {
            for (const hit of hits) {
              if (
                hit.city_state !== query &&
                slugify(hit.city_state) === slugify(query)
              ) {
                await vm.updateAutocompleteInstanceState(hit.city_state);
                break;
              }
            }
          }
        }

        return hits;
      },
      onSelect({ item }) {
        if (vm.isHomepage) {
          vm.isSubmitted = false;
        }
        vm.updateAutocompleteInstanceState(item.city_state);
      },
      templates: {
        item({ item, components, html }) {
          addOrRemoveHiddenClassToAutoCompletePanel('remove');
          if (!isZipCodeSearch(query)) {
            return html`
              <div class="aa-ItemWrapper text-sm">
                <div class="aa-ItemContent">
                  <div class="aa-ItemContentBody">
                    <div class="aa-ItemContentTitle">
                      ${components.Highlight({
                        hit: item,
                        attribute: 'city_state',
                      })}
                    </div>
                  </div>
                </div>
              </div>
            `;
          } else {
            addOrRemoveHiddenClassToAutoCompletePanel('add');
          }
        },
      },
    },
  ]);
};

const reshapeResults = query => {
  return function runReshape(...rawSources) {
    const sources = rawSources.flat().filter(Boolean);
    const seen = new Set();
    const states = {};

    return sources.map(source => {
      const cityStateSeparator = ',';
      const cities = source.getItems().filter(item => {
        const cityState = item.city_state;
        const hasSeen = seen.has(cityState);

        seen.add(cityState);
        if (
          item?._highlightResult?.state?.value.includes('__aa-highlight__') ||
          isStateAbbrSearch(query)
        ) {
          const state = isStateAbbrSearch(query)
            ? statesWithAbbr[query.toUpperCase()]
            : cityState.split(cityStateSeparator)[1].trim();


          if (item.state.toLowerCase() === state.toLowerCase()) {
            states[state] = {
              ...item,
              city_state: state,
              ...(item?._highlightResult?.state
                ? {
                    _highlightResult: {
                      city_state: item._highlightResult.state,
                    },
                  }
                : {}),
            };
          }
        }

        return !hasSeen;
      });

      const statesItems = Object.values(states).map(state => state);
      statesItems.sort((a, b) => a.city_state.localeCompare(b.city_state));

      if (isZipCodeSearch(query)) {
        const cityIndex = cities.findIndex(_city => _city.code === query);
        if (cityIndex > -1) {
          cities[cityIndex] = {
            ...cities[cityIndex],
            _highlightResult: {
              city_state: {
                value: `__aa-highlight__${cities[cityIndex].city_state}__/aa-highlight__`,
              },
            },
          };
        }
        // zipCodeItems.push({
        //   city_state: query,
        //   _highlightResult: {
        //     city_state: {
        //       value: `__aa-highlight__${query}__/aa-highlight__`,
        //     },
        //   },
        // });
      }

      return {
        ...source,
        getItems() {
          return isStateSearch(query)
            ? [...statesItems, ...cities]
            : [...cities, ...statesItems];
        },
      };
    });
  };
};

const updateAutocompletePanelPosition = (
  newComponentPosition,
  autocompletePanel,
) => {
  if (
    autocompletePanel.getBoundingClientRect().left !== newComponentPosition.left
  ) {
    autocompletePanel.style.left = `${newComponentPosition.left}px`;
  }
};

const getQueryFromInstantSearchInstance = instantSearchInstance => {
  return (
    instantSearchInstance?.mainIndex?.getHelper()?.state?.query ||
    instantSearchInstance?.helper?.state?.query ||
    ''
  );
};

export default {
  props: {
    page: {
      type: String,
      default: 'job',
    },
    paramProp: {
      default: undefined,
    },
  },
  mixins: [
    createWidgetMixin({ connector: connectSearchBox }),
    HandleSearchScrollMixin,
  ],
  data() {
    return {
      params : this.paramProp ? JSON.parse(this.paramProp) : {},
      autocompletePanel: null,
      autocompleteQuery: '',
      citiesWithHyphen: {},
      componentPosition: null,
      initialComponentPosition: null,
      intervalId: null,
      isLoading: false,
      isLocationsPage: false,
      isStateAbbrQuery: false,
      isSubmitted: false,
      locationState: '',
      selectedFiltersCount: 0,
      isHomepage: this.page === 'home',
      isCompanyPage: this.page === 'company',
    };
  },
  methods: {
    getSelectedFiltersCount(items) {
      this.selectedFiltersCount = items.reduce(
        (total, refinement) => total + refinement.refinements.length,
        0,
      );

      return this.selectedFiltersCount;
    },
    performPageSpecificAction(autocompleteQuery) {
      if (this.isCompanyPage) {
        this.updateAutocompleteInstanceState(autocompleteQuery);
      } else {
        this.goToJobsPage();
      }
    },
    async goToJobsPage() {
      const query = await this.sanitizeAndUpdateQuery(this.autocompleteQuery);
      const queryParameters = new URL(location).searchParams;
      queryParameters.set('query', encodeURIComponent(query));
      const queryString = this.autocompleteQuery
        ? `?${queryParameters.toString()}`
        : '';
      window.location = `/jobs/${queryString}`;
    },
    handleComponentScroll() {
      const componentPosition = this.$el.getBoundingClientRect();
      this.componentPosition = this.$el.getBoundingClientRect();

      if (this.isHomepage || this.isCompanyPage) {
        return;
      }

      if (document.body.classList.contains('sticky-search')) {
        this.autocompletePanel.classList.add(...classesToToggle);
        if (
          componentPosition.bottom <
          this.initialComponentPosition.y - autocompletePanelYOffset &&
          window.innerWidth >= lgBreakpoint
        ) {
          this.autocompletePanel.setAttribute(
            'style',
            `top: ${
              componentPosition.bottom - autocompletePanelTopOffset
            }px !important`,
          );
        } else {
          this.autocompletePanel.removeAttribute('style', 'top');
        }
      } else {
        this.autocompletePanel.classList.remove(...classesToToggle);
        this.autocompletePanel.style.top = `${
          this.initialComponentPosition.bottom - autocompletePanelTopOffset
        }px`;
      }
    },
    async sanitizeAndUpdateQuery(query) {
      this.isStateAbbrQuery = isStateAbbrSearch(query);

      if (this.isStateAbbrQuery) {
        query = statesWithAbbr[query.toUpperCase()];
      }

      query = this.citiesWithHyphen[query] ?? query;

      /*const { city, state } = getCityQueryAndState(query);

      if (
        query &&
        ((!city && state && slugify(query) !== slugify(state)) || !state)
      ) {
        this.isLoading = true;
        const hits = await searchPostalsIndex(this, query);
        query = hits.length ? hits[0].city_state : query;
        this.isLoading = false;
      }*/

      this.updateAutocompleteQuery(query);

      return query;
    },
    setInstantSearchUiState(query) {
      if (this.isHomepage) {
        this.goToJobsPage();
      }

      const indexName = this.instantSearchInstance.indexName;
      const urlToState = parseURLToState(query, this.params);

      this.instantSearchInstance.setUiState(uiState => {
        uiState = {
          ...routeToState(indexName, { ...urlToState, query }, this.params),
          ...uiState,
        };
        return buildUiState(uiState, indexName, query);
      });
    },
    async updateAutocompleteInstanceState(query) {
      query = await this.sanitizeAndUpdateQuery(query);
      if (this.autocompleteInstance){
        this.autocompleteInstance?.setQuery(query);
        await this.autocompleteInstance?.refresh()
      }
      this.isLoading = false;
      if (
        query !== getQueryFromInstantSearchInstance(this.instantSearchInstance)
      ) {
        this.setInstantSearchUiState(query);
      }
    },
    updateAutocompletePanel() {
      this.autocompletePanel = document.querySelector('.autocomplete-panel');

      if (this.intervalId && this.autocompletePanel) {
        clearInterval(this.intervalId);
        this.intervalId = null;
      }

      if (this.autocompletePanel) {
        this.handleComponentScroll();
      }
    },
    updateAutocompleteQuery(query) {
      this.autocompleteQuery = query;
    },
  },
  mounted() {
    const vm = this;

    const { page } = getUrlParameters();

    if (page === 'locations') {
      this.isLocationsPage = true;
    }

    const initialState = {
      query: decodeURIComponent(
        getQueryFromInstantSearchInstance(vm.instantSearchInstance) ||
          new URL(window.location).searchParams.get('query') ||
          '',
      ),
    };

    if (!initialState.query) {
      const { city, state } = getCityAndStateFromUrl();
      initialState.query = getCityAndStateTitle(city, state);
    }

    this.autocompleteInstance = autocomplete({
      container: this.$refs.autocompleteContainer,
      classNames: {
        clearButton: 'autocomplete-reset hidden',
        form: 'autocomplete-form',
        input: `autocomplete-input lg:ml-10px w-full font-montserrat outline-none border-none ${
          this.isHomepage
            ? 'text-onyx text-md'
            : this.isCompanyPage
              ? 'font-montserrat text-base font-normal'
              : 'text-onyx-lightest text-sm'
        }`,
        panelLayout: 'font-montserrat',
        loadingIndicator:
          'autocomplete-submit autocomplete-loadingIndicator hidden',
        panel: `autocomplete-panel z-50 ${
          this.isHomepage
            ? '!right-4 md:!right-0 md:!w-[454px] mt-8'
            : this.isCompanyPage
            ? 'mt-6'
            : 'left-0 !right-4 lg:!right-0 !ml-4 xl:!ml-10 !mt-8 lg:!w-327px mt-8'
        }`,
        root: 'autocomplete',
        submitButton: 'autocomplete-submit hidden',
      },
      placeholder: 'Search your city, state or ZIP',
      detachedMediaQuery: 'none',
      initialState: { query: initialState.query || '' },
      // openOnFocus: true,
      reshape({ sourcesBySourceId, state }) {
        const { postals } = sourcesBySourceId;
        return [reshapeResults(state.query)(postals)];
      },
      getSources({ query }) {
        return getPostals(vm, query);
      },
      onReset() {
        vm.updateAutocompleteInstanceState('');
      },
      onStateChange({ prevState, state }) {
        if (prevState.query !== state.query) {
          vm.isLoading = !!state.query;
          vm.updateAutocompleteQuery(state.query);
        } else if (prevState.status !== 'idle' && state.status === 'idle') {
          vm.isLoading = false;
        }

        if (!vm.isCompanyPage) {
          if (!state.isOpen) {
            vm.autocompletePanel = null;
            clearInterval(vm.intervalId);
            vm.intervalId = null;
          } else if (state.isOpen && !vm.autocompletePanel && !vm.intervalId) {
            vm.intervalId = setInterval(vm.updateAutocompletePanel, 50);
          }
        }
      },
      async onSubmit({ state: { query } }) {
        vm.isSubmitted = true;
        await vm.updateAutocompleteInstanceState(query);
      },
    });

    this.initialComponentPosition = vm.$el.getBoundingClientRect();
    window.addEventListener('scroll', vm.updateAutocompletePanel);
    window.addEventListener('resize', vm.updateAutocompletePanel);

    this.$root.$on('breadcrumbStateChange', async query => {
      await this.updateAutocompleteInstanceState(query);
    });
  },
  beforeDestroy() {
    this.autocompleteInstance?.destroy();
    this.$root.$emit('autocompleteInstancedDestroyed');
  },
  watch: {
    componentPosition(newComponentPosition) {
      if (
        !newComponentPosition ||
        !this.autocompletePanel ||
        this.isCompanyPage
      ) {
        return;
      }

      updateAutocompletePanelPosition(
        newComponentPosition,
        this.autocompletePanel,
      );

      setTimeout(
        updateAutocompletePanelPosition,
        50,
        newComponentPosition,
        this.autocompletePanel,
      );
    },
    state(newState, oldState) {
      if (!oldState || oldState?.query !== newState.query) {
        const { page, city, state } = getUrlParameters();

        if (page === 'locations') {
          if (!oldState) {
            newState.query = getCityAndStateTitle(city, state);
          }

          if (state) {
            this.locationState = state;
          }
        } else if (!oldState && !newState.query) {
          const stateFromUrl = parseURLToState(undefined, this.params);
          newState.query = stateFromUrl.query;
        }

        newState.query =
          this.citiesWithHyphen[newState.query] ?? newState.query;

        this.updateAutocompleteInstanceState(newState.query);
      }
    },
  },
};
</script>

<style>
input[type='search']::-webkit-search-decoration,
input[type='search']::-webkit-search-cancel-button,
input[type='search']::-webkit-search-results-button,
input[type='search']::-webkit-search-results-decoration {
  display: none;
}
:root {
  --aa-base-unit: 16;
  --aa-spacing-factor: 1;
  --aa-spacing: calc(var(--aa-base-unit) * var(--aa-spacing-factor) * 1px);
  --aa-spacing-half: calc(var(--aa-spacing) / 2);
  --aa-panel-max-height: 650px;
  --aa-base-z-index: 9999;
  --aa-font-size: calc(var(--aa-base-unit) * 1px);
  --aa-font-family: inherit;
  --aa-font-weight-bold: 700;
  --aa-text-color-rgb: 38, 38, 39;
  --aa-text-color-alpha: 1;
  --aa-background-color-rgb: 255, 255, 255;
  --aa-background-color-alpha: 1;
  --aa-selected-color-rgb: 179, 173, 214;
  --aa-selected-color-alpha: 0.205;
  --aa-panel-shadow: 0 0 0 1px rgba(35, 38, 59, 0.1),
    0 6px 16px -4px rgba(35, 38, 59, 0.15);
  --aa-scrollbar-width: 13px;
  --aa-scrollbar-track-background-color-rgb: 234, 234, 234;
  --aa-scrollbar-track-background-color-alpha: 1;
  --aa-scrollbar-thumb-background-color-rgb: var(--aa-background-color-rgb);
  --aa-scrollbar-thumb-background-color-alpha: 1;
}
@media (hover: none) and (pointer: coarse) {
  :root {
    --aa-spacing-factor: 1.2;
  }
}
.aa-Autocomplete *,
.aa-DetachedFormContainer *,
.aa-Panel * {
  box-sizing: border-box;
}
.aa-Autocomplete,
.aa-DetachedFormContainer,
.aa-Panel {
  color: rgba(var(--aa-text-color-rgb), var(--aa-text-color-alpha));
  font-family: var(--aa-font-family);
  font-size: var(--aa-font-size);
  font-weight: 400;
  line-height: 1em;
  margin: 0;
  padding: 0;
  text-align: left;
}
.aa-Panel {
  background-color: rgba(
    var(--aa-background-color-rgb),
    var(--aa-background-color-alpha)
  );
  border-radius: calc(var(--aa-spacing) / 4);
  box-shadow: var(--aa-panel-shadow);
  overflow: hidden;
  position: absolute;
  transition: opacity 0.2s ease-in, filter 0.2s ease-in;
}
@media screen and (prefers-reduced-motion) {
  .aa-Panel {
    transition: none;
  }
}
.aa-Panel--scrollable {
  margin: 0;
  max-height: var(--aa-panel-max-height);
  overflow-x: hidden;
  overflow-y: auto;
  padding: var(--aa-spacing-half);
  scrollbar-color: rgba(
      var(--aa-scrollbar-thumb-background-color-rgb),
      var(--aa-scrollbar-thumb-background-color-alpha)
    )
    rgba(
      var(--aa-scrollbar-track-background-color-rgb),
      var(--aa-scrollbar-track-background-color-alpha)
    );
  scrollbar-width: thin;
}
.aa-Panel--scrollable::-webkit-scrollbar {
  width: var(--aa-scrollbar-width);
}
.aa-Panel--scrollable::-webkit-scrollbar-track {
  background-color: rgba(
    var(--aa-scrollbar-track-background-color-rgb),
    var(--aa-scrollbar-track-background-color-alpha)
  );
}
.aa-Panel--scrollable::-webkit-scrollbar-thumb {
  background-color: rgba(
    var(--aa-scrollbar-thumb-background-color-rgb),
    var(--aa-scrollbar-thumb-background-color-alpha)
  );
  border-radius: 9999px;
  border: 3px solid
    rgba(
      var(--aa-scrollbar-track-background-color-rgb),
      var(--aa-scrollbar-track-background-color-alpha)
    );
  border-right-width: 2px;
}
.aa-SourceHeader {
  margin: var(--aa-spacing-half) 0.5em var(--aa-spacing-half) 0;
}
.aa-SourceHeader:empty {
  display: none;
}
.aa-Item {
  align-items: center;
  border-radius: 3px;
  cursor: pointer;
  display: grid;
  min-height: calc(var(--aa-spacing) * 2.5);
  padding: calc(var(--aa-spacing-half) / 2);
}
.aa-Item[aria-selected='true'] {
  background-color: rgba(
    var(--aa-selected-color-rgb),
    var(--aa-selected-color-alpha)
  );
}
.aa-ItemContent mark {
  background: none;
  color: rgba(var(--aa-text-color-rgb), var(--aa-text-color-alpha));
  font-style: normal;
  font-weight: var(--aa-font-weight-bold);
}

.filterIsActive {
  background-color: var(--lunar) !important;
}
.badge {
  top: -13%;
  right: 4%;
  border-radius: 50%;
}
.mobile_filter {
  & svg {
    transform: translateY(30%);
    margin-top: -50%;
  }
}

@media (max-width: 1023px) {
  .autocomplete-is-scrolled {
    position: fixed !important;
  }
}

.sticky-search .autocomplete-panel {
  position: fixed;
  margin-left: 0 !important;
  left: 0 !important;
  right: 0 !important;

  @media (min-width: 1024px) {
    margin-left: 16px !important;
  }

  @media (min-width: 1280px) {
    margin-left: 40px !important;
  }
}

.includeIcon.includeIconOnly {
  padding: 0.5rem 1rem !important;
  width: 40px !important;
  height: 40px !important;
}

.vs-button--icon {
  font-size: 24px;
  z-index: auto;
}

.box-shadow-custom-shadow {
  box-shadow: var(--shadow-color-darkest);
}
</style>
